import { useEffect, useRef } from "react";
import { Heart, Briefcase, Wine, Palette, Star } from "lucide-react";
import { useLang } from "../LanguageContext";

const eventIcons = [Heart, Briefcase, Wine, Palette, Star];
const eventKeys = ["boda", "corporativo", "coctel", "exposicion", "privado"];

const EVENTOS_BG = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/xtkzxiwf_TorreAmat_Restaurant_-2.jpg";

export default function EventosSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="eventos"
      ref={ref}
      data-testid="eventos-section"
      className="relative bg-[#E8DCC8] py-24 md:py-36 overflow-hidden"
    >
      {/* Translucid background with slow drift */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 bg-parallax"
          style={{
            backgroundImage: `url(${EVENTOS_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.12,
          }}
        />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <p
            className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.eventos.caption}
          </p>
          <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
            <h2
              className="fade-in-item text-4xl md:text-5xl font-light text-[#1A1A18] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t.eventos.heading}
            </h2>
            <p
              className="fade-in-item text-base text-[#1A1A18]/65 leading-relaxed"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.eventos.subheading}
            </p>
          </div>
        </div>

        {/* Event type cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-20">
          {eventKeys.map((key, i) => {
            const Icon = eventIcons[i];
            const ev = t.eventos[key];
            return (
              <div
                key={key}
                data-testid={`event-card-${key}`}
                className="fade-in-item group bg-[#F5F0E8] p-6 border border-transparent hover:border-[#C8A96E] transition-all duration-400 cursor-default"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center border border-[#C8A96E]/30 mb-5 group-hover:border-[#C8A96E] group-hover:bg-[#C8A96E]/10 transition-all duration-300">
                  <Icon size={18} className="text-[#C8A96E]" strokeWidth={1.5} />
                </div>
                <h3
                  className="text-base font-medium text-[#1A1A18] mb-3 leading-snug"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  {ev.title}
                </h3>
                <p
                  className="text-sm text-[#1A1A18]/60 leading-relaxed"
                  style={{ fontFamily: "Lato, sans-serif" }}
                >
                  {ev.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Capacity table */}
        <div className="fade-in-item">
          <h3
            className="text-2xl md:text-3xl font-light text-[#1A1A18] mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t.eventos.capacidad.title}
          </h3>
          <div className="overflow-x-auto">
            <table
              data-testid="capacity-table"
              className="w-full border-collapse"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              <thead>
                <tr className="border-b-2 border-[#C8A96E]/60">
                  <th className="text-left py-3 pr-8 text-xs uppercase tracking-[0.25em] text-[#C8A96E] font-normal">
                    {t.eventos.capacidad.espacio}
                  </th>
                  <th className="text-center py-3 px-6 text-xs uppercase tracking-[0.25em] text-[#C8A96E] font-normal">
                    {t.eventos.capacidad.banquete}
                  </th>
                  <th className="text-center py-3 pl-6 text-xs uppercase tracking-[0.25em] text-[#C8A96E] font-normal">
                    {t.eventos.capacidad.coctel}
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.eventos.capacidad.rows.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-b border-[#C8A96E]/20 transition-colors hover:bg-[#C8A96E]/5 ${
                      i === 0 ? "font-medium" : ""
                    }`}
                  >
                    <td className="py-4 pr-8 text-sm text-[#1A1A18]">{row[0]}</td>
                    <td className="py-4 px-6 text-center text-sm text-[#1A1A18]/80">
                      {row[1]}
                    </td>
                    <td className="py-4 pl-6 text-center text-sm text-[#1A1A18]/80">
                      {row[2]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
